/* eslint-env browser */
/* New PDP — vanilla JS controllers
 *  - Gallery (slides + dots + thumbs + arrows + swipe)
 *  - Quantity stepper
 *  - Variant picker → updates hidden `id`, price, sticky price, URL
 *  - AJAX add to cart (with cart drawer fallback to /cart)
 *  - Sticky CTA add: forwards to main add-to-cart
 */

(function () {
  'use strict';

  /** Dedupe simultaneous Judge.me widget fetches (same shop, product, token, per_page). */
  const judgeMeHtmlPromises = new Map();

  function judgeMeNpDebugEnabled() {
    try {
      return typeof location !== 'undefined' && /[?&]np_judge=1(?:&|$)/.test(location.search);
    } catch (e) {
      return false;
    }
  }

  const formatMoney = (cents) => {
    if (typeof cents !== 'number') cents = parseInt(cents, 10);
    if (isNaN(cents)) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format || '${{amount}}');
    }
    return '₪' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  };

  /**
   * If the product has a video: first slide/thumb/dot = first non-video, second = first video,
   * then remaining media in original order. If all media are video, falls back to moving the
   * first video to index 1. Skips when order already matches.
   */
  function reorderGalleryVideoSecond(gallery) {
    const slidesWrap = gallery.querySelector('[data-gallery-slides]');
    if (!slidesWrap) return;

    const slides = Array.from(slidesWrap.querySelectorAll('[data-slide-index]'));
    const n = slides.length;
    if (n < 2) return;

    const isVideo = (el) => {
      const t = el.getAttribute('data-media-type');
      return t === 'video' || t === 'external_video';
    };

    let V = -1;
    let N = -1;
    slides.forEach((el, i) => {
      if (V < 0 && isVideo(el)) V = i;
      if (N < 0 && !isVideo(el)) N = i;
    });
    if (V < 0) return;

    let orderedIdx;
    if (N >= 0) {
      orderedIdx = [N, V];
      for (let i = 0; i < n; i++) {
        if (i !== N && i !== V) orderedIdx.push(i);
      }
    } else {
      orderedIdx = [];
      for (let i = 0; i < n; i++) {
        if (i !== V) orderedIdx.push(i);
      }
      orderedIdx.splice(1, 0, V);
    }

    const unchanged = orderedIdx.every((idx, pos) => idx === pos);
    if (unchanged) return;

    const reorderByIndex = (elements, parent) => {
      if (elements.length !== n) return;
      orderedIdx.forEach((i) => parent.appendChild(elements[i]));
    };

    reorderByIndex(slides, slidesWrap);

    const dotsWrap = gallery.querySelector('[data-gallery-dots]');
    if (dotsWrap) {
      const dots = Array.from(dotsWrap.querySelectorAll('[data-dot-index]'));
      reorderByIndex(dots, dotsWrap);
    }

    const thumbsWrap = gallery.querySelector('[data-gallery-thumbs]');
    if (thumbsWrap) {
      const thumbs = Array.from(thumbsWrap.querySelectorAll('[data-thumb-index]'));
      reorderByIndex(thumbs, thumbsWrap);
    }

    const reindex = (selector, attr) => {
      gallery.querySelectorAll(selector).forEach((el, i) => {
        el.setAttribute(attr, String(i));
      });
    };
    reindex('[data-slide-index]', 'data-slide-index');
    reindex('[data-dot-index]', 'data-dot-index');
    reindex('[data-thumb-index]', 'data-thumb-index');

    gallery.querySelectorAll('.new-pdp__slide.is-active').forEach((el) => el.classList.remove('is-active'));
    gallery.querySelectorAll('.new-pdp__dot.is-active').forEach((el) => el.classList.remove('is-active'));
    gallery.querySelectorAll('.new-pdp__thumb.is-active').forEach((el) => el.classList.remove('is-active'));
    const firstSlide = slidesWrap.querySelector('.new-pdp__slide');
    const firstDot = gallery.querySelector('[data-gallery-dots] [data-dot-index]');
    const firstThumb = gallery.querySelector('[data-gallery-thumbs] [data-thumb-index]');
    if (firstSlide) firstSlide.classList.add('is-active');
    if (firstDot) firstDot.classList.add('is-active');
    if (firstThumb) firstThumb.classList.add('is-active');

    const counterCurrent = gallery.querySelector('[data-gallery-counter] [data-current]');
    if (counterCurrent) counterCurrent.textContent = '1';
  }

  /** One lightbox shell on `document.body`; z-index above `.new-pdp__sticky` (200). */
  const NP_IMG_LB_ID = 'new-pdp-image-lightbox';
  let npImgLbState = {
    shell: null,
    opener: null,
    bodyOverflow: '',
    htmlOverflow: '',
    onKeydown: null,
  };

  function closeNewPdpImageLightbox() {
    const { shell, opener, bodyOverflow, htmlOverflow, onKeydown } = npImgLbState;
    if (!shell || !shell.classList.contains('is-open')) return;
    shell.classList.remove('is-open');
    shell.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = bodyOverflow;
    document.documentElement.style.overflow = htmlOverflow;
    if (onKeydown) {
      document.removeEventListener('keydown', onKeydown, true);
      npImgLbState.onKeydown = null;
    }
    const img = shell.querySelector('.new-pdp-lightbox__img');
    if (img) {
      img.removeAttribute('src');
      img.removeAttribute('srcset');
      img.alt = '';
    }
    npImgLbState.opener = null;
    if (opener && typeof opener.focus === 'function') {
      try {
        opener.focus();
      } catch (e) {
        /* ignore */
      }
    }
  }

  function openNewPdpImageLightbox(url, alt) {
    if (!url) return;
    let shell = document.getElementById(NP_IMG_LB_ID);
    if (!shell) {
      shell = document.createElement('div');
      shell.id = NP_IMG_LB_ID;
      shell.className = 'new-pdp-lightbox';
      shell.setAttribute('role', 'dialog');
      shell.setAttribute('aria-modal', 'true');
      shell.setAttribute('aria-hidden', 'true');
      shell.innerHTML =
        '<button type="button" class="new-pdp-lightbox__backdrop" data-np-lb-close aria-label="Close"></button>' +
        '<div class="new-pdp-lightbox__panel">' +
        '<button type="button" class="new-pdp-lightbox__close" data-np-lb-close aria-label="Close">' +
        '<span aria-hidden="true">&times;</span>' +
        '</button>' +
        '<img class="new-pdp-lightbox__img" alt="" loading="eager" decoding="async" />' +
        '</div>';
      document.body.appendChild(shell);

      shell.addEventListener('click', (e) => {
        if (e.target.closest('[data-np-lb-close]')) closeNewPdpImageLightbox();
      });
    }

    npImgLbState.shell = shell;
    npImgLbState.opener = document.activeElement;
    npImgLbState.bodyOverflow = document.body.style.overflow;
    npImgLbState.htmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    const img = shell.querySelector('.new-pdp-lightbox__img');
    if (img) {
      img.alt = alt || '';
      img.src = url;
    }

    shell.setAttribute('aria-hidden', 'false');
    shell.classList.add('is-open');

    const onKeydown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeNewPdpImageLightbox();
      }
    };
    npImgLbState.onKeydown = onKeydown;
    document.addEventListener('keydown', onKeydown, true);

    const closeBtn = shell.querySelector('.new-pdp-lightbox__close');
    if (closeBtn) {
      requestAnimationFrame(() => {
        try {
          closeBtn.focus();
        } catch (e2) {
          /* ignore */
        }
      });
    }
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('shopify:section-unload', () => {
      closeNewPdpImageLightbox();
    });
  }

  function initGallery(root) {
    const gallery = root.querySelector('[data-gallery]');
    if (!gallery) return;

    reorderGalleryVideoSecond(gallery);

    const slides = Array.from(gallery.querySelectorAll('[data-slide-index]'));
    const dots = Array.from(gallery.querySelectorAll('[data-dot-index]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-thumb-index]'));
    const counter = gallery.querySelector('[data-gallery-counter]');
    const counterCurrent = counter ? counter.querySelector('[data-current]') : null;
    const prevBtn = gallery.querySelector('[data-gallery-prev]');
    const nextBtn = gallery.querySelector('[data-gallery-next]');

    if (!slides.length) return;

    let active = 0;

    const zoomMode = (gallery.getAttribute('data-np-image-zoom') || 'lightbox').trim();
    let syncZoomSlidesA11y = function noopZoomA11y() {};

    if (zoomMode !== 'off') {
      syncZoomSlidesA11y = () => {
        slides.forEach((s) => {
          if (!s.hasAttribute('data-zoom-url')) return;
          s.setAttribute('role', 'button');
          const lab = s.getAttribute('data-zoom-aria');
          if (lab) s.setAttribute('aria-label', lab);
          s.tabIndex = s.classList.contains('is-active') ? 0 : -1;
        });
      };
    }

    const goTo = (index) => {
      const total = slides.length;
      active = ((index % total) + total) % total;
      slides.forEach((s, i) => s.classList.toggle('is-active', i === active));
      dots.forEach((d, i) => d.classList.toggle('is-active', i === active));
      thumbs.forEach((t, i) => t.classList.toggle('is-active', i === active));
      if (counterCurrent) counterCurrent.textContent = String(active + 1);
      syncZoomSlidesA11y();
    };

    dots.forEach((d) => {
      d.addEventListener('click', () => goTo(parseInt(d.dataset.dotIndex, 10)));
    });
    thumbs.forEach((t) => {
      t.addEventListener('click', () => goTo(parseInt(t.dataset.thumbIndex, 10)));
    });
    if (prevBtn) prevBtn.addEventListener('click', () => goTo(active - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => goTo(active + 1));

    // Touch swipe (mobile)
    const main = gallery.querySelector('[data-gallery-main]');
    if (main) {
      let startX = 0;
      let deltaX = 0;
      let isTouching = false;

      main.addEventListener('touchstart', (e) => {
        isTouching = true;
        startX = e.touches[0].clientX;
        deltaX = 0;
      }, { passive: true });

      main.addEventListener('touchmove', (e) => {
        if (!isTouching) return;
        deltaX = e.touches[0].clientX - startX;
      }, { passive: true });

      main.addEventListener('touchend', () => {
        if (!isTouching) return;
        isTouching = false;
        const threshold = 40;
        // RTL: swipe-left should go to NEXT (visually next in reading order = forward in array)
        if (Math.abs(deltaX) > threshold) {
          if (deltaX < 0) goTo(active + 1);
          else goTo(active - 1);
        }
      });
    }

    syncZoomSlidesA11y();

    if (zoomMode !== 'off' && main && gallery.dataset.npLightboxInit !== '1') {
      gallery.dataset.npLightboxInit = '1';

      const openZoomFromSlide = (slide) => {
        if (!slide || !slide.hasAttribute('data-zoom-url')) return;
        const url = slide.getAttribute('data-zoom-url');
        const alt = slide.getAttribute('data-zoom-alt') || '';
        openNewPdpImageLightbox(url, alt);
      };

      main.addEventListener('click', (e) => {
        if (
          e.target.closest(
            '[data-gallery-prev], [data-gallery-next], [data-gallery-dots], [data-gallery-counter]'
          )
        ) {
          return;
        }
        const slide = e.target.closest('.new-pdp__slide');
        if (!slide || !slide.classList.contains('is-active') || !slide.hasAttribute('data-zoom-url')) return;
        if (!e.target.closest('img')) return;
        e.preventDefault();
        openZoomFromSlide(slide);
      });

      main.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const slide = e.target.closest('.new-pdp__slide');
        if (!slide || !slide.classList.contains('is-active') || !slide.hasAttribute('data-zoom-url')) return;
        e.preventDefault();
        openZoomFromSlide(slide);
      });
    }
  }

  function initQuantity(root) {
    const input = root.querySelector('[data-qty-input]');
    const dec = root.querySelector('[data-qty-decrement]');
    const inc = root.querySelector('[data-qty-increment]');
    if (!input) return;

    const setValue = (v) => {
      const min = parseInt(input.min || '1', 10);
      const next = Math.max(min, parseInt(v, 10) || min);
      input.value = String(next);
    };

    if (dec) dec.addEventListener('click', () => setValue(parseInt(input.value, 10) - 1));
    if (inc) inc.addEventListener('click', () => setValue(parseInt(input.value, 10) + 1));
    input.addEventListener('change', () => setValue(input.value));
  }

  function initVariantPicker(root) {
    const variantsScript = root.querySelector('[data-variants-json]');
    if (!variantsScript) return;
    let variants;
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      return;
    }

    const radios = Array.from(root.querySelectorAll('input[type="radio"][data-option-position]'));
    const variantInput = root.querySelector('[data-variant-id-input]');
    const priceCurrent = root.querySelector('[data-price-current]');
    const priceCompare = root.querySelector('[data-price-compare]');
    const priceDiscount = root.querySelector('[data-price-discount]');
    const stickyPrice = root.querySelector('[data-sticky-price]');
    const addBtn = root.querySelector('[data-add-btn]');
    const addText = root.querySelector('[data-add-text]');

    if (!radios.length) return;

    const getSelectedOptions = () => {
      const opts = [];
      radios.forEach((r) => {
        if (r.checked) {
          const pos = parseInt(r.dataset.optionPosition, 10);
          opts[pos - 1] = r.value;
        }
      });
      return opts;
    };

    const findVariant = (selected) => {
      return variants.find((v) =>
        v.options.every((opt, i) => opt === selected[i])
      );
    };

    const updateForVariant = (variant) => {
      if (variant) {
        if (variantInput) {
          variantInput.value = variant.id;
          variantInput.disabled = !variant.available;
        }
        if (priceCurrent) priceCurrent.textContent = formatMoney(variant.price);
        if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

        if (priceCompare && priceDiscount) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            priceCompare.textContent = formatMoney(variant.compare_at_price);
            priceCompare.style.display = '';
            const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
            priceDiscount.textContent = '-' + pct + '%';
            priceDiscount.style.display = '';
          } else {
            priceCompare.style.display = 'none';
            priceDiscount.style.display = 'none';
          }
        }

        if (addBtn) addBtn.disabled = !variant.available;
        if (addText) {
          const soldOut = (window.variantStrings && window.variantStrings.soldOut) || 'אזל מהמלאי';
          const addLabel = (root.dataset.addLabel) || (window.variantStrings && window.variantStrings.addToCart) || 'הוסיפו לעגלה';
          addText.textContent = variant.available ? addLabel : soldOut;
        }

        // Update URL with selected variant
        if (window.history && window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      } else {
        if (variantInput) variantInput.disabled = true;
        if (addBtn) addBtn.disabled = true;
      }
    };

    // Capture original add-to-cart label so we can restore it
    if (addText) {
      root.dataset.addLabel = addText.textContent.trim();
    }

    radios.forEach((r) => {
      r.addEventListener('change', () => {
        // Update the visible option value next to label
        const group = r.closest('[data-variant-group]');
        if (group) {
          group.querySelectorAll('.new-pdp__variant-option').forEach((label) => {
            const input = label.querySelector('input');
            label.classList.toggle('is-selected', !!(input && input.checked));
          });
          const valueEl = group.querySelector('.new-pdp__variant-value');
          if (valueEl) valueEl.textContent = r.value;
        }
        const selected = getSelectedOptions();
        const v = findVariant(selected);
        updateForVariant(v);
      });
    });
  }

  /** Line item property for gift greeting (shows on packing slip / admin). */
  const GIFT_MESSAGE_PROPERTY = 'הודעת מתנה';

  function initGiftMessage(root) {
    const cb = root.querySelector('[data-gift-checkbox]');
    const fields = root.querySelector('[data-gift-fields]');
    const ta = root.querySelector('[data-gift-textarea]');
    if (!cb || !fields) return;
    cb.addEventListener('change', () => {
      fields.hidden = !cb.checked;
      if (!cb.checked && ta) ta.value = '';
    });
  }

  function resetGiftMessage(root) {
    const cb = root.querySelector('[data-gift-checkbox]');
    const fields = root.querySelector('[data-gift-fields]');
    const ta = root.querySelector('[data-gift-textarea]');
    if (cb) cb.checked = false;
    if (fields) fields.hidden = true;
    if (ta) ta.value = '';
  }

  function initAddToCart(root) {
    const form = root.querySelector('.new-pdp__form');
    const addBtn = root.querySelector('[data-add-btn]');
    const addText = root.querySelector('[data-add-text]');
    if (!form || !addBtn) return;

    const cartType = (window.Shopify && window.Shopify.cartType) || 'page';

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (addBtn.disabled) return;

      const giftCb = root.querySelector('[data-gift-checkbox]');
      const giftTa = root.querySelector('[data-gift-textarea]');
      const giftEnabled = !!(giftCb && giftCb.checked);
      const giftText = giftTa ? giftTa.value.trim() : '';
      const giftVariantAttr = root.getAttribute('data-gift-variant-id');
      const giftVariantId = giftVariantAttr ? giftVariantAttr.trim() : '';

      if (giftEnabled && !giftText) {
        const msg = root.getAttribute('data-gift-empty-error') || 'יש למלא טקסט ברכה';
        alert(msg);
        return;
      }

      const formData = new FormData(form);
      // Same section ids as product-form (cart-drawer has no HTML id — cartDrawer.id is empty).
      const cartDrawer = document.querySelector('cart-drawer');
      const sectionsToBootstrap = [];
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        cartDrawer.getSectionsToRender().forEach((s) => sectionsToBootstrap.push(s.id));
      } else {
        if (cartDrawer) sectionsToBootstrap.push('cart-drawer');
        if (document.getElementById('cart-icon-bubble')) sectionsToBootstrap.push('cart-icon-bubble');
      }
      const sectionsParam = sectionsToBootstrap.length ? sectionsToBootstrap.join(',') : '';
      const sectionsUrl = window.location.pathname;

      const originalText = addText ? addText.textContent : '';
      addBtn.disabled = true;
      if (addText) addText.textContent = 'מוסיף לעגלה...';

      const useGiftAddonJson =
        giftEnabled &&
        giftText &&
        giftVariantId &&
        String(giftVariantId) !== String(formData.get('id'));

      let fetchOpts;
      if (useGiftAddonJson) {
        const mainId = parseInt(formData.get('id'), 10);
        const qty = parseInt(formData.get('quantity'), 10);
        const quantity = !isNaN(qty) && qty > 0 ? qty : 1;
        const items = [
          {
            id: mainId,
            quantity,
            properties: { [GIFT_MESSAGE_PROPERTY]: giftText },
          },
          {
            id: parseInt(giftVariantId, 10),
            quantity,
            properties: { [GIFT_MESSAGE_PROPERTY]: giftText },
          },
        ];
        const payload = { items };
        if (sectionsParam) {
          payload.sections = sectionsParam;
          payload.sections_url = sectionsUrl;
        }
        fetchOpts = {
          method: 'POST',
          headers: {
            Accept: 'application/javascript',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        };
      } else {
        if (giftEnabled && giftText) formData.append('properties[' + GIFT_MESSAGE_PROPERTY + ']', giftText);
        if (sectionsParam) {
          formData.append('sections', sectionsParam);
          formData.append('sections_url', sectionsUrl);
        }
        fetchOpts = {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body: formData,
        };
      }

      try {
        const res = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', fetchOpts);
        const data = await res.json();

        if (data.status && data.status >= 400) {
          throw new Error(data.description || data.message || 'Error');
        }

        // Update drawer + bubble like the theme ProductForm (renderContents opens the drawer)
        if (cartDrawer && data.sections && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(data);
          publishCartUpdate();
        } else if (cartDrawer && data.sections) {
          if (data.sections['cart-icon-bubble']) {
            const bubbleHTML = new DOMParser()
              .parseFromString(data.sections['cart-icon-bubble'], 'text/html')
              .querySelector('#shopify-section-cart-icon-bubble');
            const target = document.getElementById('shopify-section-cart-icon-bubble');
            if (bubbleHTML && target) target.innerHTML = bubbleHTML.innerHTML;
          }
          if (data.sections['cart-drawer']) {
            const newDrawerHTML = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
            const newDrawer = newDrawerHTML.querySelector('cart-drawer');
            if (newDrawer) {
              const cartDrawerInner = cartDrawer.querySelector('.drawer__inner');
              const newInner = newDrawer.querySelector('.drawer__inner');
              if (cartDrawerInner && newInner) cartDrawerInner.replaceWith(newInner);
            }
          }
          if (typeof cartDrawer.open === 'function') cartDrawer.open();
          else {
            cartDrawer.classList.add('active');
            document.body.classList.add('overflow-hidden');
          }
          publishCartUpdate();
        } else if (cartType === 'page') {
          window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        } else {
          publishCartUpdate();
        }

        if (cartDrawer && cartDrawer.classList.contains('is-empty')) {
          cartDrawer.classList.remove('is-empty');
        }

        if (addBtn) {
          addBtn.classList.add('is-success');
          if (addText) addText.textContent = '✓ נוסף לעגלה';
          resetGiftMessage(root);
          setTimeout(() => {
            addBtn.classList.remove('is-success');
            if (addText) addText.textContent = originalText;
            addBtn.disabled = false;
          }, 1600);
        }
      } catch (err) {
        addBtn.disabled = false;
        if (addText) addText.textContent = originalText;
        console.error('[new-pdp] add to cart failed:', err);
        alert(err.message || 'Error adding to cart');
      }
    });

    // Sticky CTA → forward to main form submit
    const stickyAdd = root.querySelector('[data-sticky-add]');
    if (stickyAdd) {
      stickyAdd.addEventListener('click', () => {
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      });
    }

    initStickyVisibility(root, addBtn);
  }

  function publishCartUpdate() {
    try {
      if (window.PUB_SUB_EVENTS && window.publish) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'new-pdp' });
      }
    } catch (e) {
      /* no-op */
    }
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  function syncStickyBodyPadding() {
    const hasSticky = !!document.querySelector('.new-pdp__sticky.is-visible');
    document.body.classList.toggle('has-new-pdp-sticky', hasSticky);
  }

  function setStickyVisible(sticky, visible) {
    if (!sticky) return;
    sticky.classList.toggle('is-visible', visible);
    sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
    syncStickyBodyPadding();
  }

  function initStickyVisibility(root, addBtn) {
    const sticky = root.querySelector('[data-sticky-bar]');
    if (!sticky || !addBtn) return;

    const buttonFullyInView = () => {
      const rect = addBtn.getBoundingClientRect();
      return rect.top >= 0 && rect.bottom <= window.innerHeight;
    };

    setStickyVisible(sticky, !buttonFullyInView());

    if (typeof IntersectionObserver !== 'function') {
      window.addEventListener('scroll', () => setStickyVisible(sticky, !buttonFullyInView()), { passive: true });
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setStickyVisible(sticky, entry.intersectionRatio < 1);
      },
      { threshold: 1 }
    );
    io.observe(addBtn);
  }

  /** @param {string} base embed URL */
  function npVideoIframeAutoplayUrl(base) {
    if (!base) return '';
    let u;
    try {
      u = new URL(base, window.location.href);
    } catch (e) {
      try {
        u = new URL(base.startsWith('//') ? 'https:' + base : base);
      } catch {
        const sep = base.includes('?') ? '&' : '?';
        return base + sep + 'autoplay=1&mute=1&playsinline=1';
      }
    }
    const h = u.hostname.replace(/^www\./, '');
    if (h.includes('youtube.com') || h.includes('youtube-nocookie.com')) {
      u.searchParams.set('autoplay', '1');
      u.searchParams.set('mute', '1');
      u.searchParams.set('playsinline', '1');
      return u.toString();
    }
    if (h.includes('vimeo.com') || h === 'player.vimeo.com') {
      u.searchParams.set('autoplay', '1');
      u.searchParams.set('muted', '1');
      return u.toString();
    }
    const sep = base.includes('?') ? '&' : '?';
    return base + sep + 'autoplay=1&mute=1&playsinline=1';
  }

  let npVideoClickBound = false;
  /** Hide overlay / show player and play (YouTube iframe, HTML5 file, Shopify video). External opens new tab. */
  function initNewPdpVideoCards() {
    if (npVideoClickBound) return;
    npVideoClickBound = true;
    document.addEventListener(
      'click',
      (ev) => {
        const btn = ev.target && ev.target.closest && ev.target.closest('.new-pdp__video-play');
        if (!btn || btn.disabled) return;
        const card = btn.closest('[data-video-card]');
        if (!card || card.classList.contains('is-video-playing')) return;
        const mode = card.getAttribute('data-video-mode') || '';
        const wrap = card.querySelector('[data-video-player]');
        if (!wrap) return;

        if (mode === 'external') {
          const href = card.getAttribute('data-external-href');
          if (href) window.open(href, '_blank', 'noopener,noreferrer');
          ev.preventDefault();
          return;
        }

        if (!mode) return;

        ev.preventDefault();
        card.classList.add('is-video-playing');
        wrap.hidden = false;

        if (mode === 'iframe') {
          const base = card.getAttribute('data-iframe-src');
          const iframe = card.querySelector('[data-video-iframe]');
          if (iframe instanceof HTMLIFrameElement && base) {
            iframe.src = npVideoIframeAutoplayUrl(base);
          }
        } else if (mode === 'html5') {
          const vid = card.querySelector('[data-video-html5]');
          if (vid instanceof HTMLVideoElement) {
            const fs = card.getAttribute('data-file-src');
            if (fs && !vid.src) vid.src = fs;
            vid.play().catch(() => {
              vid.muted = true;
              vid.play().catch(() => {});
            });
          }
        } else if (mode === 'shopify') {
          const vid = wrap.querySelector('video');
          if (vid instanceof HTMLVideoElement) {
            vid.play().catch(() => {
              vid.muted = true;
              vid.play().catch(() => {});
            });
          }
        }
      },
      true
    );
  }

  /** Match section/new-pdp-reviews star row (head uses 16px). */
  function npReviewsStarsHtml(rating, size) {
    const n = Math.max(0, Math.min(5, Math.floor(Number(rating)) || 0));
    const empty = 5 - n;
    const on = '#EF9F27';
    const off = '#d1d5db';
    const poly =
      '12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2';
    let h = '';
    for (let i = 0; i < n; i++) {
      h +=
        `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="${on}" stroke="${on}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="${poly}"></polygon></svg>`;
    }
    for (let i = 0; i < empty; i++) {
      h +=
        `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${off}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="${poly}"></polygon></svg>`;
    }
    return h;
  }

  /**
   * Parse Judge.me embedded widget markup for aggregate rating/count.
   * @returns {{ avg: number, count: number } | null}
   */
  function parseJudgeMeWidgetSummary(widgetHtml) {
    if (!widgetHtml) return null;
    const doc = new DOMParser().parseFromString(widgetHtml, 'text/html');
    const widg =
      doc.querySelector('.jdgm-rev-widg') ||
      doc.querySelector('[data-average-rating][data-number-of-reviews]');
    if (!widg) return null;

    const rawAvg = widg.getAttribute('data-average-rating');
    const rawCount = widg.getAttribute('data-number-of-reviews');
    const avg = rawAvg != null ? parseFloat(rawAvg) : NaN;
    const count = rawCount != null ? parseInt(rawCount, 10) : NaN;
    if (!Number.isFinite(count)) return null;
    return { avg, count };
  }

  /**
   * PDP main column: `.new-pdp__rating` — Judge.me aggregates from parsed widget HTML.
   */
  function syncJudgeMeInlineRating(summary, rootEl) {
    const root =
      rootEl instanceof Element
        ? rootEl
        : document.querySelector('.new-pdp--main [data-np-inline-rating]');
    if (!root) return;
    if (!summary || !Number.isFinite(summary.count)) {
      root.hidden = true;
      return;
    }

    const count = summary.count;
    const avg = summary.avg;

    const wrap = root.querySelector('[data-np-inline-stars]');
    const link = root.querySelector('[data-np-inline-rating-link]');
    const reviewsLabel =
      ((root && root.getAttribute('data-reviews-label')) || '').trim() || 'ביקורות';

    if (count < 1 || !Number.isFinite(avg)) {
      root.hidden = true;
      return;
    }

    root.hidden = false;
    const displayScore = Math.round(avg * 10) / 10;
    if (wrap) wrap.innerHTML = npReviewsStarsHtml(avg, 16);
    if (link) link.textContent = `${count} ${reviewsLabel} · (${displayScore})`;
  }

  /**
   * Fill new-pdp__reviews-head from Judge.me widget summary (parsed JSON widget HTML).
   * Write / close popup is owned by Judge.me — do not proxy clicks or force form display.
   */
  function syncJudgeMeReviewsHead(sectionEl, summary) {
    const head = sectionEl && sectionEl.querySelector('[data-judgeme-head]');
    if (!head) return;

    const wrap = head.querySelector('[data-judgeme-review-count-wrap]');
    const summaryRow = head.querySelector('[data-judgeme-summary]');
    const scoreEl = head.querySelector('[data-judgeme-score]');
    const starsEl = head.querySelector('[data-judgeme-stars]');

    if (!summary || !Number.isFinite(summary.count)) {
      if (wrap) wrap.textContent = '';
      if (summaryRow) summaryRow.hidden = true;
      return;
    }

    const avg = summary.avg;
    const count = summary.count;
    if (wrap) wrap.textContent = ' (' + count + ')';

    if (count < 1 || !Number.isFinite(avg)) {
      if (scoreEl) scoreEl.textContent = '';
      if (starsEl) starsEl.innerHTML = npReviewsStarsHtml(0, 16);
      if (summaryRow) summaryRow.hidden = false;
      return;
    }

    const displayScore = Math.round(avg * 10) / 10;
    if (scoreEl) scoreEl.textContent = String(displayScore);
    if (starsEl) starsEl.innerHTML = npReviewsStarsHtml(avg, 16);
    if (summaryRow) summaryRow.hidden = false;
  }

  /**
   * Judge.me Public API widget HTML. Token in query avoids CORS preflight.
   * @returns {Promise<string>}
   */
  function fetchJudgeMeProductWidgetHtml(shop, productId, token, perPageRaw) {
    const n = parseInt(perPageRaw, 10);
    const perPage = Number.isFinite(n) && n > 0 ? n : 10;
    const key = `${shop}\0${productId}\0${token}\0${perPage}`;
    if (judgeMeHtmlPromises.has(key)) return judgeMeHtmlPromises.get(key);

    const url = new URL('https://api.judge.me/api/v1/widgets/product_review');
    url.searchParams.set('shop_domain', shop);
    url.searchParams.set('external_id', productId);
    url.searchParams.set('per_page', String(perPage));
    url.searchParams.set('page', '1');
    url.searchParams.set('api_token', token);

    if (judgeMeNpDebugEnabled()) {
      const redacted = url.toString().replace(/([?&]api_token=)[^&]*/, '$1(redacted)');
      console.info('[new-pdp] Judge.me fetch', redacted);
    }

    const p = fetch(url.toString(), { credentials: 'omit' })
      .then((response) => {
        if (!response.ok) throw new Error('Judge.me HTTP ' + response.status);
        return response.json();
      })
      .then((data) => {
        const html = data && typeof data.widget === 'string' ? data.widget : '';
        if (!html) throw new Error('Judge.me: missing widget HTML');
        return html;
      });

    judgeMeHtmlPromises.set(key, p);
    return p;
  }

  /**
   * Judge.me — reviews mount(s) (`data-judgeme-root`) plus optional main PDP inline rating
   * (`[data-judgeme-inline]` on `.new-pdp__rating`). Merges by shop/product/token and uses max per_page so one HTTP request suffices when both sections share credentials.
   */
  function initJudgeMeProductWidgets() {
    /** @type {Map<string, { shop: string, productId: string, token: string, mounts: Array, inlines: Array, maxPerPage: number }>} */
    const groups = new Map();

    function bumpGroup(shop, productId, token, perPageStr) {
      const key = `${shop}\x00${productId}\x00${token}`;
      const n = parseInt(perPageStr, 10);
      const pg = Number.isFinite(n) && n > 0 ? n : 10;
      if (!groups.has(key)) {
        groups.set(key, {
          shop,
          productId,
          token,
          maxPerPage: pg,
          mounts: [],
          inlines: [],
        });
      }
      const g = groups.get(key);
      g.maxPerPage = Math.max(g.maxPerPage, pg);
      return g;
    }

    document.querySelectorAll('[data-judgeme-root]').forEach((mount) => {
      if (mount.dataset.judgemeInit === '1') return;
      const token = (mount.getAttribute('data-token') || '').trim();
      if (!token) return;

      const sectionEl = mount.closest('section.new-pdp');
      const fallback = sectionEl && sectionEl.querySelector('[data-judgeme-fallback]');
      const shop = (mount.getAttribute('data-shop-domain') || '').trim();
      const productId = (mount.getAttribute('data-product-id') || '').trim();
      const perPage = (mount.getAttribute('data-per-page') || '10').trim();
      if (!shop || !productId) {
        mount.hidden = true;
        if (fallback) fallback.hidden = false;
        return;
      }

      mount.dataset.judgemeInit = '1';
      mount.hidden = false;
      if (fallback) fallback.hidden = true;

      bumpGroup(shop, productId, token, perPage).mounts.push({ mount, sectionEl, fallback });
    });

    document.querySelectorAll('.new-pdp--main [data-np-inline-rating][data-judgeme-inline]').forEach((root) => {
      if (root.dataset.judgemeInlineInit === '1') return;
      const token = (root.getAttribute('data-token') || '').trim();
      if (!token) return;

      const main = root.closest('.new-pdp--main');
      if (!main) return;
      const productId = (main.getAttribute('data-product-id') || '').trim();
      const shop = (root.getAttribute('data-shop-domain') || '').trim();
      const perPage = (root.getAttribute('data-per-page') || '10').trim();
      if (!shop || !productId) return;

      root.dataset.judgemeInlineInit = '1';
      bumpGroup(shop, productId, token, perPage).inlines.push({ root });
    });

    if (judgeMeNpDebugEnabled()) {
      const mains = Array.from(document.querySelectorAll('.new-pdp.new-pdp--main'));
      console.info('[new-pdp] Judge.me PDP scan', {
        mainSections: mains.length,
        mainDataAttrTrue: mains.filter((el) => el.dataset.npJudgemeInline === 'true').length,
        inlineTargets: document.querySelectorAll('.new-pdp--main [data-judgeme-inline]').length,
        reviewMountsUntouched: Array.from(document.querySelectorAll('[data-judgeme-root]')).filter(
          (el) => el.dataset.judgemeInit !== '1'
        ).length,
        fetchGroupsQueued: groups.size,
      });
    }

    groups.forEach((group) => {
      const { shop, productId, token, maxPerPage, mounts, inlines } = group;
      if (!mounts.length && !inlines.length) return;

      fetchJudgeMeProductWidgetHtml(shop, productId, token, maxPerPage)
        .then((html) => {
          const jmSummary = parseJudgeMeWidgetSummary(html);
          mounts.forEach(({ mount, sectionEl }) => {
            // Parse-only: do not paint API HTML (official widget is the UI)
            syncJudgeMeReviewsHead(sectionEl, jmSummary);
            mount.innerHTML = '';
            mount.hidden = true;
          });
          inlines.forEach(({ root }) => {
            syncJudgeMeInlineRating(jmSummary, root);
          });
        })
        .catch(() => {
          if (judgeMeNpDebugEnabled()) {
            console.warn('[new-pdp] Judge.me request failed', { shop, productId });
          }
          mounts.forEach(({ mount }) => {
            mount.innerHTML = '';
            mount.hidden = true;
          });
          inlines.forEach(({ root }) => {
            if (root) root.hidden = true;
          });
        });
    });
  }

  function init() {
    document.querySelectorAll('.new-pdp').forEach((root) => {
      if (root.dataset.npInit === '1') return;
      root.dataset.npInit = '1';
      initGallery(root);
      initQuantity(root);
      initVariantPicker(root);
      initGiftMessage(root);
      initAddToCart(root);
    });
    syncStickyBodyPadding();
    initNewPdpVideoCards();
    initJudgeMeProductWidgets();
    queueMicrotask(() => {
      initJudgeMeProductWidgets();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init in Shopify theme editor
  document.addEventListener('shopify:section-load', init);
  document.addEventListener('shopify:section-unload', syncStickyBodyPadding);
})();
